import { useEffect, useRef, useState } from "react";

/**
 * Camera stream that starts when `enabled` flips true and stops on disable or
 * unmount (ticket 02). Drive-session hard requirement: the stream must start
 * once per open and never restart on render — so unlike a mount-once ref guard
 * (which StrictMode's double-invoke kills: the first in-flight getUserMedia is
 * cancelled, the second invocation skips restarting, and the screen stays
 * black), the stream lifecycle is bound to `enabled` exactly like
 * `useQrScanner`'s open-gating. Tracks stop on disable; state resets so a
 * reopen gets a fresh stream. A ref (not the setState updater) owns the live
 * stream so tracks are stopped even when the component unmounts mid-flight.
 */
export function useCameraStream(enabled = true) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStream(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      setError("unsupported");
      return;
    }
    mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((media) => {
        if (cancelled) {
          media.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = media;
        setStream(media);
      })
      .catch(() => {
        if (!cancelled) setError("denied");
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    };
  }, [enabled]);

  return { stream, error };
}

/** First supported recorder mime, or undefined to let MediaRecorder pick. */
export function pickRecorderMime(candidates: string[]): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return candidates.find((mime) => MediaRecorder.isTypeSupported(mime));
}
